import { gql } from "@apollo/client";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { orderBy, startCase } from "lodash";
import { useMemo, useState } from "react";

import PermissionOrHide from "~/components/common/PermissionOrHide";
import {
  GetVisitDetailsQuery,
  useCreateErrandVisitResourceMutation,
  useDeleteErrandVisitResourceMutation,
  useVisitErrandOptionsQuery,
} from "~/graphql/__generated__/graphql";
import { Permission, useOrganizationalUnitSegmentationEnabled } from "~/hooks/permissions";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";

type Visit = NonNullable<GetVisitDetailsQuery["visitSingle"]>;

type Props = {
  visit: Visit;
};

const VisitErrandResources = ({ visit }: Props) => {
  const { errandTypes, serviceLines } = useOrgConfig();
  const { timeLabelWithZone } = useZonedDateFormat();
  const organizationalUnitSegmentationEnabled = useOrganizationalUnitSegmentationEnabled();
  const serviceLineId = visit.episode?.serviceLineId;
  const organizationalUnitId = visit.episode?.organizationalUnitId;
  const organizationalUnitKnown = !organizationalUnitSegmentationEnabled || organizationalUnitId !== undefined;
  const serviceLine = serviceLineId ? serviceLines[serviceLineId] : undefined;
  const [errandTypeId, setErrandTypeId] = useState<number | "">("");
  const [errandLocationId, setErrandLocationId] = useState<number | "">("");
  const [providerId, setProviderId] = useState<number | "">("");
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<
    { resource: Visit["visitResources"][number]; anchor: HTMLElement } | null
  >(null);

  const presentErrandTypeIds = useMemo(() => new Set(
    (visit.visitResources ?? [])
      .filter(resource => resource.errandStatus)
      .map(resource => resource.resourceRequirement?.errandType?.id)
      .filter((id): id is number => id != null)
  ), [visit.visitResources]);

  const availableErrandTypes = useMemo(() => {
    const linkedIds = new Set(serviceLine?.errandTypeServiceLines.map(link => link.errandTypeId) ?? []);
    return orderBy(
      Object.values(errandTypes).filter(errandType =>
        errandType?.active
        && linkedIds.has(errandType.id)
        && errandType.visitResourceRequirements.length === 1
        && !presentErrandTypeIds.has(errandType.id)
      ),
      "name",
    );
  }, [errandTypes, presentErrandTypeIds, serviceLine]);

  const selectedErrandType = errandTypeId === "" ? undefined : errandTypes[errandTypeId];
  const requirement = selectedErrandType?.visitResourceRequirements[0];
  const allowedProviderTypeIds = new Set(requirement?.providerTypes.map(type => type.id) ?? []);

  const { data: optionsData, loading: optionsLoading, error: optionsError } = useVisitErrandOptionsQuery({
    variables: { errandTypeId: errandTypeId === "" ? -1 : errandTypeId },
    skip: errandTypeId === "",
    fetchPolicy: "network-only",
  });
  const segmentByUnit = organizationalUnitSegmentationEnabled === true
    && optionsData?.organizationalUnit.some(unit => unit.active) === true
    && organizationalUnitId != null;

  const locations = (optionsData?.errandLocation ?? []).filter(location =>
    !segmentByUnit || location.organizationalUnitId === organizationalUnitId
  );
  const locationAvailable = !segmentByUnit || locations.some(location => location.id === errandLocationId);
  const providers = (optionsData?.provider ?? []).filter(provider =>
    provider.providerTypeId != null
    && allowedProviderTypeIds.has(provider.providerTypeId)
    && (!serviceLineId || provider.serviceLineProviders.some(link => link.serviceLineId === serviceLineId))
    && (!segmentByUnit
      || provider.OrganizationUser?.organizationalUnitOrgUsers.some(
        membership => membership.organizationalUnitId === organizationalUnitId
      ))
  );
  const providerAvailable = !segmentByUnit || providers.some(provider => provider.id === providerId);

  const [create, createState] = useCreateErrandVisitResourceMutation();
  const [remove, removeState] = useDeleteErrandVisitResourceMutation();
  const refetchQueries = ["GetVisitDetails", "CommandIndexCombinedData"];
  const mutationError = createState.error ?? removeState.error;
  const errands = orderBy(
    (visit.visitResources ?? []).filter(resource => resource.errandStatus),
    "startTime",
  );

  const resetDraft = () => {
    setErrandTypeId("");
    setErrandLocationId("");
    setProviderId("");
  };

  const onCreate = async () => {
    if (errandTypeId === "" || errandLocationId === "" || providerId === ""
      || !locationAvailable || !providerAvailable || optionsLoading || optionsError) return;
    await create({
      variables: { visitId: visit.id, errandTypeId, errandLocationId, providerId },
      awaitRefetchQueries: true,
      refetchQueries,
    });
    resetDraft();
    setAdding(false);
  };

  const onRemove = async () => {
    if (!removeTarget) return;
    await remove({
      variables: { id: removeTarget.resource.id, visitId: visit.id },
      awaitRefetchQueries: true,
      refetchQueries,
    });
    setRemoveTarget(null);
  };

  const addressLabel = (resource: Visit["visitResources"][number]) =>
    resource.addressStreet
      ? [resource.addressStreet, resource.addressCity, resource.addressState].filter(Boolean).join(", ")
      : "Visit location";

  if (availableErrandTypes.length === 0 && errands.length === 0) return null;

  return (
    <Stack sx={{ mb: 2 }}>
      <Typography variant="button">Errands</Typography>
      {errands.length > 0 && (
        <Stack component="ul" spacing={1} sx={{ pl: 2, my: 0.5 }}>
          {errands.map(resource => (
            <Box component="li" key={resource.id}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box>
                  <Typography variant="body1">
                    {resource.resourceRequirement?.errandType?.name ?? "Errand"} —{" "}
                    {resource.provider?.firstName} {resource.provider?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {addressLabel(resource)} · {startCase(resource.errandStatus ?? "")} ·{" "}
                    {timeLabelWithZone(resource.startTime, true, resource.addressTimezone ?? visit.addressTimezone)}
                  </Typography>
                </Box>
                <PermissionOrHide permission={Permission.manageAnyVisit}>
                  <IconButton
                    aria-label={`Remove ${resource.resourceRequirement?.errandType?.name ?? "errand"}`}
                    size="small"
                    onClick={event => setRemoveTarget({ resource, anchor: event.currentTarget })}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </PermissionOrHide>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <PermissionOrHide permission={Permission.manageAnyVisit}>
        {availableErrandTypes.length > 0 && !adding && organizationalUnitKnown && (
          <Button
            startIcon={<AddCircleOutlineIcon />}
            size="small"
            sx={{ mt: 1, alignSelf: "flex-start" }}
            onClick={() => setAdding(true)}
          >
            Add Errand
          </Button>
        )}
        {availableErrandTypes.length > 0 && adding && organizationalUnitKnown && (
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="errand-type-label">Errand Type</InputLabel>
              <Select
                labelId="errand-type-label"
                label="Errand Type"
                value={errandTypeId}
                onChange={event => {
                  setErrandTypeId(Number(event.target.value));
                  setErrandLocationId("");
                  setProviderId("");
                }}
              >
                {availableErrandTypes.map(errandType => (
                  <MenuItem key={errandType!.id} value={errandType!.id}>{errandType!.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {errandTypeId !== "" && (
              <FormControl size="small" fullWidth disabled={optionsLoading}>
                <InputLabel id="errand-location-label">Location</InputLabel>
                <Select
                  labelId="errand-location-label"
                  label="Location"
                  value={locationAvailable ? errandLocationId : ""}
                  onChange={event => setErrandLocationId(Number(event.target.value))}
                >
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {errandTypeId !== "" && (
              <FormControl size="small" fullWidth disabled={optionsLoading}>
                <InputLabel id="errand-provider-label">Provider</InputLabel>
                <Select
                  labelId="errand-provider-label"
                  label="Provider"
                  value={providerAvailable ? providerId : ""}
                  onChange={event => setProviderId(Number(event.target.value))}
                >
                  {providers.map(provider => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.firstName} {provider.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {optionsError && <Alert severity="error">{optionsError.message}</Alert>}
            {mutationError && <Alert severity="error">{mutationError.message}</Alert>}
            <Stack direction="row" spacing={1} sx={{ alignSelf: "flex-start" }}>
              <Button
                variant="contained"
                onClick={onCreate}
                loading={createState.loading}
                disabled={errandTypeId === "" || errandLocationId === "" || providerId === ""
                  || !locationAvailable || !providerAvailable || optionsLoading || !!optionsError}
              >
                Add Errand
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  resetDraft();
                  setAdding(false);
                }}
                disabled={createState.loading}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        )}
      </PermissionOrHide>

      <Menu
        open={!!removeTarget}
        anchorEl={removeTarget?.anchor}
        onClose={() => setRemoveTarget(null)}
      >
        <MenuItem onClick={onRemove} disabled={removeState.loading}>
          Confirm Remove
          {removeState.loading ? <CircularProgress size={20} sx={{ ml: 1 }} /> : null}
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export default VisitErrandResources;

gql(`
  query VisitErrandOptions($errandTypeId: Int!)
  @permission(requires: useWebApp)
  {
    organizationalUnit {
      id
      active
    }
    errandLocation(
      orderBy: {name: { direction: asc, priority: 1 }}
      where: { errandTypeId: { eq: $errandTypeId } }
    ) {
      id
      name
      organizationalUnitId
    }
    provider(
      orderBy: {
        firstName: { direction: asc, priority: 1 }
        lastName: { direction: asc, priority: 2 }
      }
      where: { active: { eq: true } }
    ) {
      id
      firstName
      lastName
      providerTypeId
      OrganizationUser {
        organizationalUnitOrgUsers {
          organizationalUnitId
        }
      }
      serviceLineProviders {
        serviceLineId
      }
    }
  }
`);

gql(`
  mutation CreateErrandVisitResource(
    $visitId: Int!
    $errandTypeId: Int!
    $errandLocationId: Int!
    $providerId: Int!
  )
  @permission(requires: manageAnyVisit, check: [userInVisitServiceLine])
  {
    createErrandVisitResource(
      visitId: $visitId
      errandTypeId: $errandTypeId
      errandLocationId: $errandLocationId
      providerId: $providerId
    ) {
      id
    }
  }
`);

gql(`
  mutation DeleteErrandVisitResource($id: Int!, $visitId: Int!)
  @permission(requires: manageAnyVisit, check: [userInVisitServiceLine])
  {
    deleteErrandVisitResource(id: $id, visitId: $visitId) {
      id
      deleted
    }
  }
`);
